import { api } from './api';

// Medication request interface (nested within subscription)
export interface MedicationRequest {
  id: string;
  medication: string;
  dose: string;
  quantity_authorized: number;
  sig: string;
  reason_for_compounding?: string;
  day_supply: string;
  product_id?: string;
  product?: {
    id: string;
    description?: string;
    ndc?: string;
    price_plan_cost?: number;
    price_per_unit?: number;
  };
  clinicProduct?: {
    selling_price_cents?: number;
    base_price_cents?: number;
  };
}

// Subscription medication join table interface
export interface SubscriptionMedication {
  id: string;
  subscription_id: string;
  medication_request_id: string;
  amount?: number;
  medicationRequest?: MedicationRequest;
}

export interface PatientContact {
  id: string;
  patient_id: string;
  key: string;
  value: string;
}

// Subscription model interface (aligned with backend Subscription model)
export interface Subscription {
  id: string;
  patient_id: string;
  practitioner_id: string;
  clinic_id: string;
  program_id: string; // References clinic_programs table (ClinicProgram.id)
  prescription_id: string;
  
  // Core subscription fields (no prefix - matches Subscription model)
  frequency: "weekly" | "monthly" | "quarterly" | "yearly";
  status: "active" | "paused" | "cancelled" | "expired" | "pending_amendment";
  next_run_date: Date;
  auto_renew: boolean;
  amount: number;
  duration?: number;
  
  // Payment tracking
  payment_method_id?: string;
  last_payment_status?: string;
  last_payment_date?: Date;
  last_transfer_id?: string;
  retry_count?: number;
  
  // Lifecycle dates
  start_date?: Date;
  end_date?: Date;
  canceled_at?: Date;
  paused_at?: Date;
  resumed_at?: Date;
  
  // Nested relationships
  subscriptionMedications?: SubscriptionMedication[];
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    contacts?: PatientContact[];

  };
  practitioner?: {
    id: string;
    first_name: string;
    last_name: string;
    specialization?: string;
  };
  clinic?: {
    id: string;
    name: string;
  };
  program?: {
    id: string;
    title: string;
    subtitle?: string;
  };
  prescription?: {
    id: string;
    status: string;
    payment_status: string;
    total_amount?: number;
    shipping_carrier_id?: string;
    shipping_carrier_name?: string;
    shipping_service_id?: string;
    shipping_service_name?: string;
    shipping_cost_cents?: number;
    shipping_original_cost_cents?: number;
    is_free_shipment?: boolean;
    free_shipment_type?: 'global' | 'clinic_specific' | null;
  };
  
  created_at: Date;
  updated_at: Date;
}

// Keep old interface name as alias for backward compatibility
export type MedicationSubscription = Subscription;

export interface SubscriptionResponse {
  success: boolean;
  data?: Subscription;
  message?: string;
  error?: string;
}

export interface PaginatedSubscriptions {
  subscriptions: Subscription[];  // Changed from prescriptions
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SubscriptionStatusCounts {
  total: number;
  active: number;
  paused: number;
  cancelled: number;
}

// Billing history item interface
export interface BillingHistoryItem {
  id: string;
  subscription_id: string;
  amount: number;
  currency?: string;
  status: 'pending' | 'success' | 'failed' | 'cancelled';
  billing_date: Date;
  payment_date?: Date;
  failure_code?: string;
  failure_reason?: string;
  prescription_id?: string;
  transfer_id?: string;
  attempt_number?: number;
  is_retry?: boolean;
  next_retry_at?: Date;
}

// Helper function to get medication requests from subscription
export function getMedicationRequests(subscription: Subscription): MedicationRequest[] {
  if (!subscription.subscriptionMedications) return [];
  return subscription.subscriptionMedications
    .map(sm => sm.medicationRequest)
    .filter((mr): mr is MedicationRequest => mr !== undefined);
}

class SubscriptionService {
  /**
   * Get patient subscriptions
   */
  async getPatientSubscriptions(patientId: string, page = 1, limit = 10): Promise<PaginatedSubscriptions> {
    try {
      const response = await api.get<PaginatedSubscriptions>(
        `/subscriptions/patient/${patientId}?page=${page}&limit=${limit}`
      );
      return response;
    } catch (error: any) {
      console.error('Get patient subscriptions error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
    }
  }

  /**
   * Get subscription by ID
   */
  async getSubscription(subscriptionId: string): Promise<{ success: boolean; data: Subscription }> {
    try {
      const response = await api.get<{ success: boolean; data: Subscription }>(`/subscriptions/${subscriptionId}`);
      return response;
    } catch (error: any) {
      console.error('Get subscription error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription details');
    }
  }

  /**
   * Update subscription status
   */
  async updateSubscriptionStatus(
    subscriptionId: string, 
    status: "active" | "paused" | "cancelled"
  ): Promise<SubscriptionResponse> {
    try {
      const response = await api.patch<SubscriptionResponse>(
        `/subscriptions/${subscriptionId}/status`, 
        { status }
      );
      return response;
    } catch (error: any) {
      console.error('Update subscription status error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update subscription status');
    }
  }

  /**
   * Update subscription medications
   */
  async updateSubscriptionMedications(
    subscriptionId: string,
    medications: Array<{
      medication_id: string;
      dose: string;
      quantity: number;
      sig: string;
      reason: string;
      daysSupply: string;
      product_id: string;
    }>
  ): Promise<SubscriptionResponse> {
    try {
      const response = await api.patch<SubscriptionResponse>(
        `/subscriptions/${subscriptionId}/medications`,
        { medications }
      );
      return response;
    } catch (error: any) {
      console.error('Update subscription medications error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update subscription medications');
    }
  }

  /**
   * Update subscription frequency
   */
  async updateSubscriptionFrequency(
    subscriptionId: string,
    frequency: "weekly" | "monthly" | "quarterly" | "yearly"
  ): Promise<SubscriptionResponse> {
    try {
      const response = await api.patch<SubscriptionResponse>(
        `/subscriptions/${subscriptionId}/frequency`,
        { frequency }
      );
      return response;
    } catch (error: any) {
      console.error('Update subscription frequency error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update subscription frequency');
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(subscriptionId: string, reason?: string): Promise<SubscriptionResponse> {
    try {
      const response = await api.delete<SubscriptionResponse>(
        `/subscriptions/${subscriptionId}`,
        { data: { reason } }
      );
      return response;
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
    }
  }

  /**
   * Get subscription billing history
   */
  async getSubscriptionBilling(
    subscriptionId: string,
    page = 1,
    limit = 10
  ): Promise<{
    success: boolean;
    billing: BillingHistoryItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const response = await api.get(
        `/subscriptions/${subscriptionId}/billing?page=${page}&limit=${limit}`
      );
      return response;
    } catch (error: any) {
      console.error('Get subscription billing error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch billing history');
    }
  }

  /**
   * Get clinic subscription status counts
   */
  async getClinicSubscriptionCounts(clinicId: string): Promise<SubscriptionStatusCounts> {
    try {
      const response = await api.get<{ success: boolean; counts: SubscriptionStatusCounts }>(
        `/subscriptions/clinic/${clinicId}/counts`
      );
      return response.counts;
    } catch (error: any) {
      console.error('Get clinic subscription counts error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription counts');
    }
  }

  /**
   * Get clinic subscriptions (for practitioners)
   */
  async getClinicSubscriptions(
    clinicId: string,
    page = 1,
    limit = 10,
    status?: "active" | "paused" | "cancelled"
  ): Promise<PaginatedSubscriptions> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (status) {
        params.append('status', status);
      }

      const response = await api.get<PaginatedSubscriptions>(
        `/subscriptions/clinic/${clinicId}?${params.toString()}`
      );
      return response;
    } catch (error: any) {
      console.error('Get clinic subscriptions error:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch clinic subscriptions');
    }
  }

  /**
   * Trigger manual subscription refill
   */
  async triggerRefill(subscriptionId: string): Promise<SubscriptionResponse> {
    try {
      const response = await api.post<SubscriptionResponse>(
        `/subscriptions/${subscriptionId}/refill`
      );
      return response;
    } catch (error: any) {
      console.error('Trigger subscription refill error:', error);
      throw new Error(error.response?.data?.message || 'Failed to trigger subscription refill');
    }
  }

  /**
   * Enroll patient in subscription (clinic-initiated)
   * Creates program-based subscriptions - one subscription per program
   */
  async enrollPatient(data: {
    patient_id: string;
    practitioner_id: string;
    clinic_id: string;
    program_id: string;
    medications: Array<{
      product_id: string;
      medication?: string;
      dose: string;
      quantity: number;
      price?: number;
      sig?: string;
      daysSupply?: string;
      template_id?: string;
    }>;
    frequency: "weekly" | "monthly" | "quarterly";
    payment_token?: string;
    payment_method_id?: string;
    fraud_session_id?: string;
    auto_renew?: boolean;
    duration?: number;
    allergyWarningsAcknowledged?: boolean;
    shipping: {
      carrier_id: string;
      carrier_name: string;
      service_id: string;
      service_name: string;
      cost_cents: number;
      original_cost_cents: number;
      is_free_shipment: boolean;
      free_shipment_type?: 'global' | 'clinic_specific' | null;
    };
  }): Promise<{
    success: boolean;
    message?: string;
    data?: {
      prescription_id: string;
      subscriptions: Array<{
        id: string;
        program_id: string;
        amount: number;
        medications_count: number;
      }>;
      patient_id: string;
      frequency: string;
      next_run_date: Date;
      total_amount: number;
      payment: {
        success: boolean;
        transaction_id?: string;
        error?: string;
      };
    };
  }> {
    try {
      const response = await api.post('/subscriptions/enroll', data);
      return response;
    } catch (error: any) {
      console.error('Enroll patient in subscription error:', error);
      // Re-throw the API error with code intact so isStripeOnboardingRequiredError can detect it
      if (error?.code || error?.message) {
        throw error;
      }
      throw new Error('Failed to enroll patient in subscription');
    }
  }

  /**
   * Retry failed subscription payment
   */
  async retryPayment(
    subscriptionId: string, 
    newPaymentToken?: string,
    fraudSessionId?: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      transaction_id: string;
      status: string;
      failure_message?: string;
    };
  }> {
    try {
      const response = await api.post(`/subscriptions/${subscriptionId}/retry-payment`, {
        new_payment_token: newPaymentToken,
        fraud_session_id: fraudSessionId,
      });
      return response;
    } catch (error: any) {
      console.error('Retry subscription payment error:', error);
      throw new Error(error.response?.data?.message || 'Failed to retry payment');
    }
  }

  /**
   * Update subscription payment method
   */
  async updatePaymentMethod(
    subscriptionId: string,
    paymentToken: string,
    fraudSessionId?: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      payment_instrument_id: string;
      card_last4: string;
      card_brand: string;
    };
  }> {
    try {
      const response = await api.patch(`/subscriptions/${subscriptionId}/payment-method`, {
        payment_token: paymentToken,
        fraud_session_id: fraudSessionId,
      });
      return response;
    } catch (error: any) {
      console.error('Update payment method error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update payment method');
    }
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
